/**
 * Folgeunterlage SiGe-Plan — Entwurf aus der Planungsphase.
 *
 * Legt den Plankopf (`sige_projekte`) als ENTWURF an und fuellt ihn mit dem,
 * was die Stufe-3-Vorschlaege des Assistenten hergeben — nur eben auf einmal
 * statt Klick fuer Klick:
 *
 *   Plankopf        `plankopfAusStammdaten` (wie Schritt 1), Bauherr und
 *                   Adressen aus dem Steckbrief (Platzhalter „Gelände" faellt
 *                   weg), Bauzeit nach Entscheid E1 (Ausfuehrungszeitraum vor
 *                   Projektstart/-ende), „bearbeitet von" = wer anlegt.
 *   Arbeitsschritte Musterzeilen mit der Vorauswahl nach den Merkmalen (E6,
 *                   `musterVorauswahl`): unbedingt und bejaht → angelegt,
 *                   ungeklaert → angelegt und in `offen` benannt, verneint →
 *                   weggelassen. Zeilenbau `baueMusterZeilen` wie Schritt 3.
 *   Dokumente       bedingte mitgeltende Dokumente, deren Merkmal die
 *                   Planungsphase ausdruecklich bejaht
 *                   (`bedingteDokumenteNachMerkmalen`) — beim Anlegen gleich
 *                   in die Zeile gelegt, nicht als zweiter Schreibvorgang.
 *
 * Was der Plan nicht speichern kann, steht als Satz in `offen`: die
 * Baubeschreibung (keine Spalte — im Assistenten ist sie Eingabe der KI), die
 * beantworteten Fragen mit Zielobjekt `sigeplan` (der Stempel
 * „beruecksichtigt" ist Sache des Koordinators) und Anhang-II-Arbeiten ohne
 * passenden Arbeitsschritt (`anhang2OhneSchritt`, dieselbe Regel wie die
 * Vollstaendigkeitspruefung).
 *
 * ═══ HAUSREGEL ════════════════════════════════════════════════════════════
 * Rechnet und schlaegt vor — gibt nie frei (Status bleibt `entwurf`),
 * versendet nichts, legt nichts in die Ablage, bucht nichts, stempelt keine
 * Planungsfrage. Gibt es fuer das Projekt schon einen (nicht geloeschten)
 * Plan, wird NICHTS geschrieben — auch keine weitere Version.
 *
 * Aufbau: oben die puren Teile (getestet ohne Datenbank), unten die duennen
 * Supabase-Aufrufe. `legeAn` lehnt nie ab; jeder Fehler wird ein Ergebnis.
 */

import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import {
    ANLAGE_ABGEBROCHEN_TEXT,
    sigePlanVorlagenFuerAnlage,
    vorbelegungNeuerPlan,
} from '../sigePlanVorlagen';
import { baueSteckbrief, OHNE_LESEFEHLER, type Angabe, type ProjektSteckbrief } from '../projektSteckbrief/steckbrief';
import type { ProjektMerkmale } from '../../components/planungsphase/types';
import { MERKMAL_LABEL } from '../../components/planungsphase/fragenkatalog';
import { MUSTER_ZEILEN, type MusterZeile } from '../../components/sige-plan/musterVorlage';
import { musterVorauswahl } from '../../components/sige-plan/musterVorauswahl';
import {
    baueMusterZeilen,
    bedingteAlsAuftraege,
    bedingteDokumenteNachMerkmalen,
    plankopfAusStammdaten,
    plankopfZuDb,
    type PlankopfForm,
    type PlanZeitraum,
    type SchrittFuerDokumente,
} from '../../components/sige-plan/uebernahme';
import {
    baubeschreibungVorschlag,
    frageIdsNachCode,
    hinweiseAusFragen,
} from '../../components/sige-plan/planungAnbindung';
import { anhang2OhneSchritt, ANHANG2_STICHWORTE } from '../../components/sige-plan/vollstaendigkeit';
import type { BestandStand, ErzeugungsErgebnis, FolgeErzeuger, PlanungsQuelle } from './typen';

const ART = 'sigeplan' as const;
const TITEL = 'SiGe-Plan';
const PLAN_INHALT = 'Sicherheits- und Gesundheitsschutzplan';

// ═══════════════════════════════════════════════════════════════════════════
// PURE TEILE
// ═══════════════════════════════════════════════════════════════════════════

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * Tag in deutscher Schreibweise. Ein reiner ISO-Tag wird zerlegt (kein
 * `new Date`, das verschoebe ihn je Zeitzone); ein Zeitstempel wird in
 * Ortszeit gelesen. Leer bei Unbrauchbarem.
 */
export const tagDeutsch = (wert: string | null | undefined): string => {
    const s = String(wert || '').trim();
    if (!s) return '';
    const tag = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (tag) return `${tag[3]}.${tag[2]}.${tag[1]}`;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '';
    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
};

/** Liste fuer einen Satz: „A, B und C"; ab `max` Eintraegen gekuerzt. */
const aufzaehlung = (werte: readonly string[], max = 6): string => {
    const liste = werte.filter(Boolean);
    if (liste.length > max) return `${liste.slice(0, max).join(', ')} …`;
    if (liste.length <= 1) return liste.join('');
    return `${liste.slice(0, -1).join(', ')} und ${liste[liste.length - 1]}`;
};

/** Steckbrief aus der gespeicherten Planung. Der Plan existiert noch nicht —
 *  deshalb keine SiGe-Schritte und keine Lesefehler (die Quelle ist gelesen). */
export const steckbriefAusQuelle = (q: PlanungsQuelle): ProjektSteckbrief =>
    baueSteckbrief({
        metadata: q.projektDaten,
        phase: q.phase,
        fragen: q.fragen || [],
        sigeSchritte: [],
        lesefehler: { ...OHNE_LESEFEHLER },
    }, q.heuteIso);

/** Bestand → Satz, z. B. „Entwurf vom 12.10.2026". */
export const sigeplanBestandText = (zeile: {
    status?: string | null;
    created_at?: string | null;
    version_label?: string | null;
}): string => {
    const tag = tagDeutsch(zeile.created_at);
    const status = zeile.status || '';
    let text: string;
    if (status === 'geprueft') text = tag ? `geprüft, angelegt am ${tag}` : 'geprüft';
    else if (status === 'ueberarbeitet') text = tag ? `überarbeitet, angelegt am ${tag}` : 'überarbeitet';
    else text = tag ? `Entwurf vom ${tag}` : 'Entwurf';
    const label = (zeile.version_label || '').trim();
    return label ? `${text} (Fassung „${label}")` : text;
};

/** Vorauswahl der Musterzeilen nach E6 — gewählt, davon ungeklärt, verneint. */
export interface MusterAuswahl {
    /** Was angelegt wird (unbedingt, bejaht, ungeklaert). */
    gewaehlt: MusterZeile[];
    /** Davon nur vorgewaehlt, weil ein Merkmal offen ist — die „gelben" Zeilen. */
    ungeklaert: MusterZeile[];
    /** Offene Merkmale hinter den gelben Zeilen, ohne Doppelte. */
    ungeklaertMerkmale: (keyof ProjektMerkmale)[];
    /** Weggelassen, weil jedes Merkmal der Zeile ausdruecklich verneint ist. */
    verneint: MusterZeile[];
}

export const musterAuswahlNachMerkmalen = (
    merkmale: ProjektMerkmale | null,
    zeilen: readonly MusterZeile[] = MUSTER_ZEILEN,
): MusterAuswahl => {
    const vorauswahl = musterVorauswahl(zeilen, merkmale);
    const gewaehlt: MusterZeile[] = [];
    const ungeklaert: MusterZeile[] = [];
    const verneint: MusterZeile[] = [];
    const offen = new Set<keyof ProjektMerkmale>();
    vorauswahl.forEach(e => {
        const zeile = zeilen[e.index];
        if (!e.gewaehlt) { verneint.push(zeile); return; }
        gewaehlt.push(zeile);
        if (e.stand === 'ungeklaert') {
            ungeklaert.push(zeile);
            e.merkmale.forEach(k => offen.add(k));
        }
    });
    return { gewaehlt, ungeklaert, ungeklaertMerkmale: [...offen], verneint };
};

const alsText = (v: unknown): string => (typeof v === 'string' ? v : '');
const textListe = (v: unknown): string[] =>
    (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

/** Zeilen fuer `sige_arbeitsschritte` samt aufgeloesten bedingten Dokumenten. */
export interface SchrittZeilen {
    rows: Record<string, unknown>[];
    /** Bedingte Dokumente, die hinzugekommen sind — Name je Zeile gezaehlt. */
    bedingteDokumente: number;
    /** Zeilen, an denen ein bedingtes Dokument hinzugekommen ist. */
    bedingteZeilen: number;
    /** Die hinzugekommenen Namen, ohne Doppelte, in Vorschlagsreihenfolge. */
    bedingteNamen: string[];
}

/**
 * Musterzeilen → Schreibzeilen, wie Schritt 3 sie baut (Zeitfenster aus der
 * Bauzeit, unbedingte Katalogdokumente), dazu die bedingten Dokumente, deren
 * Merkmal ausdruecklich `true` ist. Die Zuordnung laeuft ueber den Index der
 * Zeile als Ersatz-Id — die Zeilen haben noch keine echte.
 */
export const schrittZeilen = (
    gewaehlt: readonly MusterZeile[],
    sigeProjektId: string,
    zeitraum: PlanZeitraum,
    merkmale: ProjektMerkmale | null,
): SchrittZeilen => {
    const { rows } = baueMusterZeilen([...gewaehlt], {
        sigeProjektId,
        zeitraum,
        startSortierung: 0,
        vorhandeneBezeichnungen: [],
    });
    const zeilen: Record<string, unknown>[] = rows.map(r => ({ ...r }));
    const schritte: SchrittFuerDokumente[] = zeilen.map((r, i) => ({
        id: String(i),
        bezeichnung: alsText(r.bezeichnung),
        gewerk: alsText(r.gewerk),
        hauptabschnitt: alsText(r.hauptabschnitt),
        schutzmassnahmen: textListe(r.schutzmassnahmen),
        vorschriften: textListe(r.vorschriften),
        mitgeltende_dokumente: textListe(r.mitgeltende_dokumente),
    }));
    const auftraege = bedingteAlsAuftraege(bedingteDokumenteNachMerkmalen(schritte, merkmale), schritte);
    const namen: string[] = [];
    let anzahl = 0;
    auftraege.forEach(a => {
        const i = Number(a.schrittId);
        if (!Number.isInteger(i) || !zeilen[i]) return;
        zeilen[i] = { ...zeilen[i], mitgeltende_dokumente: [...a.dokumente] };
        anzahl += a.hinzu.length;
        a.hinzu.forEach(n => { if (!namen.includes(n)) namen.push(n); });
    });
    return { rows: zeilen, bedingteDokumente: anzahl, bedingteZeilen: auftraege.length, bedingteNamen: namen };
};

/** Die Planzeilen, so weit die Anhang-II-Regel sie liest. */
const alsAnhangSchritte = (rows: readonly Record<string, unknown>[]): { bezeichnung: string; gewerk: string }[] =>
    rows.map(r => ({ bezeichnung: alsText(r.bezeichnung), gewerk: alsText(r.gewerk) }));

/**
 * Bauzeit nach E1 aus dem Steckbrief. Der Wert wird genommen, wenn es einen
 * gibt — auch der Rueckfall auf Projektstart/-ende, den der Assistent in
 * Schritt 1 ebenso vorbelegt; der Vorbehalt steht dann in `offen`.
 */
export const bauzeitSaetze = (
    beginn: Angabe<string>,
    ende: Angabe<string>,
): { zeitraum: { von: string; bis: string }; uebernommen: string[]; offen: string[] } => {
    const von = beginn.wert || '';
    const bis = ende.wert || '';
    const uebernommen: string[] = [];
    const offen: string[] = [];
    const quelle = (a: Angabe<string>) => (a.stand === 'belegt' ? 'dem Ausführungszeitraum' : 'Projektstart/-ende');
    if (von && bis) {
        const gleich = quelle(beginn) === quelle(ende);
        uebernommen.push(gleich
            ? `Bauzeit ${tagDeutsch(von)} bis ${tagDeutsch(bis)} aus ${quelle(beginn)} übernommen.`
            : `Bauzeit ${tagDeutsch(von)} bis ${tagDeutsch(bis)} übernommen (Beginn aus ${quelle(beginn)}, Ende aus ${quelle(ende)}).`);
    } else if (von || bis) {
        uebernommen.push(von
            ? `Baubeginn ${tagDeutsch(von)} aus ${quelle(beginn)} übernommen.`
            : `Bauende ${tagDeutsch(bis)} aus ${quelle(ende)} übernommen.`);
    }
    if ((von && beginn.stand !== 'belegt') || (bis && ende.stand !== 'belegt')) {
        offen.push('Bauzeit im Plankopf prüfen: der Ausführungszeitraum ist nicht gepflegt, Projektstart/-ende kann die Planungsphase einschließen.');
    }
    const fehlt = [!von ? 'Baubeginn' : '', !bis ? 'Bauende' : ''].filter(Boolean);
    if (fehlt.length) {
        const grund = [!von ? beginn.grund : '', !bis ? ende.grund : ''].filter(Boolean)[0];
        offen.push(`${aufzaehlung(fehlt)} im Plankopf eintragen${grund ? ` (${grund.replace(/\.$/, '')})` : ''}. Ohne Bauzeit bekommen die Arbeitsschritte keine Zeiträume.`);
    }
    return { zeitraum: { von, bis }, uebernommen, offen };
};

/** Alles, was ohne Datenbank feststeht: Kopf, Auswahl, Saetze. */
export interface SigeplanEntwurf {
    form: PlankopfForm;
    zeitraum: PlanZeitraum;
    /** Merkmale der Planungsphase (Kopie) — Eingang der Vorauswahl. */
    merkmale: ProjektMerkmale;
    auswahl: MusterAuswahl;
    /** Bejahte Anhang-II-Nummern laut Planungsphase. */
    anhang2Nummern: number[];
    uebernommen: string[];
    offen: string[];
}

/** Plankopf aus Stammdaten und Steckbrief. */
export const plankopfAusPlanung = (q: PlanungsQuelle, sb: ProjektSteckbrief, zeitraum: { von: string; bis: string }): PlankopfForm => {
    const basis = plankopfAusStammdaten(
        q.projektDaten ?? undefined,
        undefined,
        // „bearbeitet von" ist, wer anlegt — vor dem Koordinator der Stammdaten.
        { bearbeitet_name: (q.bearbeiter || '').trim() },
        q.heuteIso,
    );
    return {
        ...basis,
        bauherr_name: sb.bauherr.name.wert || basis.bauherr_name || '',
        bauherr_adresse: sb.bauherr.anschrift.wert || '',
        baumassnahme_name: sb.projekt.name.wert || basis.baumassnahme_name || '',
        baumassnahme_adresse: sb.projekt.adresse.wert || '',
        zeitraum_von: zeitraum.von,
        zeitraum_bis: zeitraum.bis,
    };
};

export const sigeplanEntwurf = (q: PlanungsQuelle): SigeplanEntwurf => {
    const sb = steckbriefAusQuelle(q);
    const uebernommen: string[] = [];
    const offen: string[] = [];

    const bauzeit = bauzeitSaetze(sb.ausfuehrung.beginn, sb.ausfuehrung.ende);
    const form = plankopfAusPlanung(q, sb, bauzeit.zeitraum);

    const gefuellt = [
        form.bauherr_name ? 'Bauherr' : '',
        form.baumassnahme_name ? 'Baumaßnahme' : '',
        form.baumassnahme_adresse ? 'Adresse' : '',
        form.plan_nr ? `Plan-Nr. ${form.plan_nr}` : '',
        form.sigeko_name ? 'Koordinator' : '',
    ].filter(Boolean);
    uebernommen.push(gefuellt.length
        ? `Plankopf aus den Projektdaten angelegt: ${aufzaehlung(gefuellt, 10)}.`
        : 'Plankopf angelegt; die Projektdaten lieferten keine Angaben.');
    uebernommen.push(...bauzeit.uebernommen);
    const kopfFehlt = [!form.bauherr_name ? 'Bauherr' : '', !form.baumassnahme_name ? 'Baumaßnahme' : ''].filter(Boolean);
    if (kopfFehlt.length) offen.push(`Plankopf ergänzen: ${aufzaehlung(kopfFehlt)} fehlt in den Projektdaten.`);
    offen.push(...bauzeit.offen);

    // Ohne Planungsphase (`phase` fehlt trotz Vertrag) waere jede Zeile
    // unbedingt — wie der Assistent ohne Planung.
    const merkmale: ProjektMerkmale = { ...sb.merkmale };
    const auswahl = musterAuswahlNachMerkmalen(q.phase ? merkmale : null);

    const beschreibung = baubeschreibungVorschlag(sb);
    if (beschreibung) {
        offen.push(`Baubeschreibung (${beschreibung.quelle}${beschreibung.grund ? `: ${beschreibung.grund.replace(/\.$/, '')}` : ''}) `
            + 'hat im Plan kein Feld; im Assistenten Schritt 2 daraus weitere Arbeitsschritte vorschlagen lassen.');
    }

    const hinweise = hinweiseAusFragen(sb.fragen.sigeplan, frageIdsNachCode(q.fragen || []))
        .filter(h => !h.uebernommenAm);
    if (hinweise.length) {
        offen.push(`${hinweise.length} beantwortete Frage(n) der Planungsphase im Assistenten prüfen und als berücksichtigt stempeln: `
            + `${aufzaehlung(hinweise.map(h => h.code))}.`);
    }

    if (sb.anhang2.offen.length) {
        offen.push(`Anhang II Nr. ${sb.anhang2.offen.join(', ')} ${sb.anhang2.offen.length > 1 ? 'sind' : 'ist'} in der Planungsphase noch ungeklärt.`);
    }

    return {
        form,
        zeitraum: bauzeit.zeitraum,
        merkmale,
        auswahl,
        anhang2Nummern: [...(sb.anhang2.nummern.wert || [])],
        uebernommen,
        offen,
    };
};

/** Schreibform des Kopfs: dieselbe wie Schritt 1 (`plankopfZuDb`) plus
 *  Plotterbreite der Firma, Status ausdruecklich `entwurf`. */
export const kopfNutzlast = (
    projectId: string,
    form: PlankopfForm,
    vorbelegung: { plot_breite_mm: number },
): Record<string, unknown> => ({
    ...plankopfZuDb(form, projectId),
    ...vorbelegung,
    status: 'entwurf',
});

/** Rueckfall wie im Assistenten: fehlt eine Spalte, wenigstens die Grunddaten. */
export const minimaleKopfNutzlast = (
    projectId: string,
    form: PlankopfForm,
    vorbelegung: { plot_breite_mm: number },
): Record<string, unknown> => ({
    project_id: projectId,
    baumassnahme_name: (form.baumassnahme_name || '').trim() || 'SiGe-Projekt',
    plan_inhalt: PLAN_INHALT,
    status: 'entwurf',
    ...vorbelegung,
});

/** Saetze zu den angelegten Arbeitsschritten. */
export const schrittSaetze = (
    auswahl: MusterAuswahl,
    zeilen: SchrittZeilen,
    anhang2Nummern: readonly number[],
): { uebernommen: string[]; offen: string[] } => {
    const uebernommen: string[] = [];
    const offen: string[] = [];
    uebernommen.push(`${zeilen.rows.length} Arbeitsschritt(e) aus der Muster-Vorlage nach den Merkmalen der Planungsphase angelegt.`);
    if (auswahl.verneint.length) {
        uebernommen.push(`${auswahl.verneint.length} Musterzeile(n) weggelassen, weil das Merkmal in der Planungsphase verneint ist.`);
    }
    if (zeilen.bedingteDokumente) {
        uebernommen.push(`${zeilen.bedingteDokumente} mitgeltende(s) Dokument(e) an ${zeilen.bedingteZeilen} Arbeitsschritt(e) gehängt, `
            + `weil die Planungsphase das Merkmal bejaht: ${aufzaehlung(zeilen.bedingteNamen, 4)}.`);
    }
    if (auswahl.ungeklaert.length) {
        // Semikolon statt Komma: die Merkmalnamen tragen selbst Kommas
        // („Erdarbeiten, Baugrube, Gräben").
        const labels = auswahl.ungeklaertMerkmale.map(k => MERKMAL_LABEL[k] || k);
        const liste = labels.length > 4 ? `${labels.slice(0, 4).join('; ')} …` : labels.join('; ');
        offen.push(`${auswahl.ungeklaert.length} Arbeitsschritt(e) stehen nur, weil ein Merkmal ungeklärt ist (${liste}); im Plan prüfen.`);
    }
    offen.push(...anhangOhneSchrittSaetze(anhang2Nummern, zeilen.rows));
    return { uebernommen, offen };
};

/** Anhang-II-Arbeiten laut Planung ohne passenden Arbeitsschritt (Regel der Vollstaendigkeitspruefung). */
export const anhangOhneSchrittSaetze = (
    nummern: readonly number[],
    rows: readonly Record<string, unknown>[],
): string[] => {
    const ohne = anhang2OhneSchritt(nummern, alsAnhangSchritte(rows));
    if (!ohne.length) return [];
    const liste = ohne.map(n => `Nr. ${n} (${ANHANG2_STICHWORTE[n].kurz})`).join(', ');
    return [`Anhang II ${liste} laut Planungsphase zutreffend, aber ohne passenden Arbeitsschritt; im Plan ergänzen.`];
};

/** Der Rest des Assistenten, den die Planung nicht hergibt. */
export const REST_IM_ASSISTENTEN = 'Gefährdungen, Plan-Blöcke (Notrufplan, Legende, Piktogramme) und die Prüfung im Assistenten ab Schritt 4 ergänzen.';

/**
 * Nachkontrolle nach dem Anlegen. Laufsperre und Bestandspruefung gelten nur
 * je Fenster; `sige_projekte` hat keinen Unique-Index je Projekt (Fassungen
 * sind eigene Zeilen), und eine Datenbanksperre gibt es nicht. Lesen zwei
 * Fenster gleichzeitig „kein Plan", legen beide an. Unmittelbar vor dem
 * Anlegen gab es keinen Plan — stehen danach MEHR als einer (der eigene
 * eingeschlossen) nicht geloescht da, hat ein zweites Fenster mitangelegt.
 * Geloescht wird nichts automatisch: welcher Entwurf bleibt, entscheidet der
 * Mensch. `null` (nicht lesbar) oder hoechstens einer → kein Satz.
 */
export const doppelPlanSatz = (anzahl: number | null): string | null =>
    anzahl !== null && anzahl > 1
        ? `Für das Projekt bestehen jetzt ${anzahl} SiGe-Pläne. Vermutlich wurde zeitgleich in einem zweiten Fenster angelegt. Den doppelten Entwurf im SiGe-Plan löschen.`
        : null;

// ═══════════════════════════════════════════════════════════════════════════
// DATENBANK — duenn
// ═══════════════════════════════════════════════════════════════════════════

const T_PLAN = 'sige_projekte';
const T_SCHRITT = 'sige_arbeitsschritte';

const ohneProjekt = (projectId: string): boolean => !projectId || projectId === 'temp-id';

/**
 * Liest den neuesten nicht geloeschten Plan des Projekts. Legt nichts an.
 * `.is('deleted_at', null)` wie Editor und Handbuch: `sige_projekte` loescht
 * weich, eine verworfene Fassung ist kein Bestand.
 */
const leseBestand = async (projectId: string): Promise<BestandStand> => {
    if (ohneProjekt(projectId)) {
        return { status: 'fehler', fehler: 'Ohne gespeichertes Projekt gibt es keinen SiGe-Plan.' };
    }
    try {
        const { data, error } = await supabase
            .from(T_PLAN)
            .select('id, status, created_at, version_label')
            .eq('project_id', projectId)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .limit(1);
        if (error) return { status: 'fehler', fehler: `Der SiGe-Plan konnte nicht gelesen werden: ${describeError(error)}` };
        const zeile = (data || [])[0];
        return zeile ? { status: 'vorhanden', beschreibung: sigeplanBestandText(zeile) } : { status: 'keiner' };
    } catch (e: unknown) {
        return { status: 'fehler', fehler: `Der SiGe-Plan konnte nicht gelesen werden: ${describeError(e)}` };
    }
};

/** Id des eben angelegten Plans nachlesen — RLS liefert die INSERT-Rueckgabe
 *  nicht immer (siehe Assistent, Schritt 1). */
const neuesteId = async (projectId: string): Promise<string | null> => {
    const { data, error } = await supabase
        .from(T_PLAN)
        .select('id')
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
    if (error || !data) return null;
    const id = (data as { id?: unknown }).id;
    return typeof id === 'string' && id ? id : null;
};

/** Obergrenze der Nachkontrolle — gezaehlt wird nur „mehr als einer". */
const DOPPEL_LESEGRENZE = 50;

/** Zahl der nicht geloeschten Plaene des Projekts. Liest nur; `null`, wenn
 *  nicht lesbar — die Nachkontrolle macht dann keine Aussage. */
const zaehlePlaene = async (projectId: string): Promise<number | null> => {
    try {
        const { data, error } = await supabase
            .from(T_PLAN)
            .select('id')
            .eq('project_id', projectId)
            .is('deleted_at', null)
            .limit(DOPPEL_LESEGRENZE);
        if (error || !Array.isArray(data)) return null;
        return data.length;
    } catch {
        return null;
    }
};

type KopfErgebnis =
    | { ok: true; id: string | null; minimal: boolean; fehler: string | null }
    | { ok: false; fehler: string };

const legeKopfAn = async (
    projectId: string,
    voll: Record<string, unknown>,
    minimal: Record<string, unknown>,
): Promise<KopfErgebnis> => {
    const versuche = async (nutzlast: Record<string, unknown>): Promise<{ id: string | null; fehler: unknown }> => {
        const { data, error } = await supabase.from(T_PLAN).insert(nutzlast).select('id').maybeSingle();
        if (error) return { id: null, fehler: error };
        const id = (data as { id?: unknown } | null)?.id;
        return { id: typeof id === 'string' && id ? id : await neuesteId(projectId), fehler: null };
    };
    const erst = await versuche(voll);
    if (!erst.fehler) return { ok: true, id: erst.id, minimal: false, fehler: null };
    const zweit = await versuche(minimal);
    if (!zweit.fehler) return { ok: true, id: zweit.id, minimal: true, fehler: describeError(erst.fehler) };
    return { ok: false, fehler: `${describeError(erst.fehler)} / Rückfall: ${describeError(zweit.fehler)}` };
};

const fehlerErgebnis = (fehler: string): ErzeugungsErgebnis => ({ status: 'fehler', art: ART, fehler });

const legeAnOhneSperre = async (q: PlanungsQuelle): Promise<ErzeugungsErgebnis> => {
    if (ohneProjekt(q?.projectId)) return fehlerErgebnis('Ohne gespeichertes Projekt kann kein SiGe-Plan angelegt werden.');

    // Erst die Firmenvorgaben (wartet ggf. bis 5 s), DANN der Bestand — so
    // liegt die Pruefung unmittelbar vor dem Schreiben.
    const anlage = await sigePlanVorlagenFuerAnlage();
    if (anlage.abgebrochen === true) return fehlerErgebnis(ANLAGE_ABGEBROCHEN_TEXT);
    const vorbelegung = vorbelegungNeuerPlan(anlage.vorlagen);

    const vorher = await leseBestand(q.projectId);
    if (vorher.status === 'vorhanden') return { status: 'vorhanden', art: ART, beschreibung: vorher.beschreibung };
    if (vorher.status === 'fehler') return fehlerErgebnis(vorher.fehler);

    const entwurf = sigeplanEntwurf(q);
    const uebernommen = [...entwurf.uebernommen];
    const offen = [...entwurf.offen];

    const kopf = await legeKopfAn(
        q.projectId,
        kopfNutzlast(q.projectId, entwurf.form, vorbelegung),
        minimaleKopfNutzlast(q.projectId, entwurf.form, vorbelegung),
    );
    if (kopf.ok === false) return fehlerErgebnis(`Der SiGe-Plan konnte nicht angelegt werden: ${kopf.fehler}`);

    // Nachkontrolle erst ganz am Ende: je spaeter gelesen, desto sicherer ist
    // ein gleichzeitiges zweites Anlegen schon sichtbar (das spaetere der
    // beiden Fenster sieht beide Plaene in jedem Fall).
    const angelegt = async (saetze: string[], rest: string[]): Promise<ErzeugungsErgebnis> => {
        const doppelt = doppelPlanSatz(await zaehlePlaene(q.projectId));
        return { status: 'angelegt', art: ART, uebernommen: saetze, offen: doppelt ? [doppelt, ...rest] : rest };
    };

    if (!anlage.geladen) {
        offen.push(`Die Firmenvorgabe für die Plotterbreite war nicht geladen. Angelegt mit ${vorbelegung.plot_breite_mm} mm; im Plankopf prüfen.`);
    }

    let kopfSaetze = uebernommen;
    if (kopf.minimal) {
        // Nur Grunddaten: die Saetze zum Kopf stimmen nicht mehr.
        kopfSaetze = ['Plan nur mit Grunddaten als Entwurf angelegt.'];
        offen.unshift(`Plankopf im Assistenten Schritt 1 ergänzen; das vollständige Anlegen schlug fehl: ${kopf.fehler}`);
    } else {
        kopfSaetze.unshift('SiGe-Plan als Entwurf angelegt.');
    }

    if (!kopf.id) {
        offen.push('Der Plan wurde angelegt, ließ sich aber nicht zurücklesen. Arbeitsschritte im Assistenten wählen.');
        offen.push(...anhangOhneSchrittSaetze(entwurf.anhang2Nummern, []));
        offen.push(REST_IM_ASSISTENTEN);
        return angelegt(kopfSaetze, offen);
    }

    // Ohne vollstaendigen Kopf traegt der Plan keine Bauzeit — dann auch
    // keine daraus gerechneten Schrittzeitraeume.
    const zeitraum: PlanZeitraum = kopf.minimal ? {} : entwurf.zeitraum;
    const zeilen = schrittZeilen(entwurf.auswahl.gewaehlt, kopf.id, zeitraum, q.phase ? entwurf.merkmale : null);
    let schrittFehler: string | null = null;
    if (zeilen.rows.length) {
        try {
            const { error } = await supabase.from(T_SCHRITT).insert(zeilen.rows);
            if (error) schrittFehler = describeError(error);
        } catch (e: unknown) {
            schrittFehler = describeError(e);
        }
    }

    if (schrittFehler) {
        offen.push(`Arbeitsschritte im Assistenten wählen (Schritt 3); das Anlegen der Musterzeilen schlug fehl: ${schrittFehler}`);
        offen.push(...anhangOhneSchrittSaetze(entwurf.anhang2Nummern, []));
    } else if (zeilen.rows.length) {
        const s = schrittSaetze(entwurf.auswahl, zeilen, entwurf.anhang2Nummern);
        kopfSaetze.push(...s.uebernommen);
        offen.push(...s.offen);
    } else {
        offen.push('Keine Musterzeile vorgewählt. Arbeitsschritte im Assistenten wählen (Schritt 3).');
    }
    offen.push(REST_IM_ASSISTENTEN);
    return angelegt(kopfSaetze, offen);
};

/** Laufsperre je Projekt: ein zweiter Aufruf waehrend des ersten bekommt
 *  dasselbe Ergebnis statt einen zweiten Plan anzulegen. */
const laufend = new Map<string, Promise<ErzeugungsErgebnis>>();

const legeAn = (q: PlanungsQuelle): Promise<ErzeugungsErgebnis> => {
    const schluessel = q?.projectId || '';
    const da = laufend.get(schluessel);
    if (da) return da;
    const lauf = legeAnOhneSperre(q)
        .catch((e: unknown) => fehlerErgebnis(`Der SiGe-Plan konnte nicht angelegt werden: ${describeError(e)}`))
        .finally(() => { laufend.delete(schluessel); });
    laufend.set(schluessel, lauf);
    return lauf;
};

export const sigeplanErzeuger: FolgeErzeuger = {
    art: ART,
    titel: TITEL,
    bestand: leseBestand,
    legeAn,
};
